// src/stores/auth.js
import { defineStore } from 'pinia'
import { useNotificationStore } from './useNotificationStore'
import apiClient from '@/api/axios'
import Cookies from 'js-cookie'
import dayjs from 'dayjs'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    phone: '',
    name: '',
    acc_no: '',
    accessToken: '',
    bonus: [],
    exp: null,
    balance: 0,
    tempPassword: '',
    expirationTimer: null,
    justVerified: false,
    depositInitiated: false,
    depositConfirmed: false,
    lastBetSuccess: false,
    lastBetStake: 0,
    lastDepositAmount: null,
    lastBetOdds: 0,
    lastBetPayout: 0,
    // Shared bet functionality
    isSharedBet: false,
    lastSharedBetCode: '',
    // Authentication flow tracking
    currentFlow: null, // 'registration' | 'forgot-password' | null
    flowData: {
      fromRegistration: false,
      fromForgotPassword: false,
      canAccessAccountCreated: false,
    },
  }),

  getters: {
    isAuthenticated: (state) => {
      if (!state.accessToken || !state.exp) return false

      // Handle both string and number formats for exp
      let expirationDate
      if (typeof state.exp === 'string') {
        // Handle format like "20251109203458" (YYYYMMDDHHMMSS)
        if (state.exp.length === 14) {
          const year = state.exp.substring(0, 4)
          const month = state.exp.substring(4, 6)
          const day = state.exp.substring(6, 8)
          const hour = state.exp.substring(8, 10)
          const minute = state.exp.substring(10, 12)
          const second = state.exp.substring(12, 14)
          expirationDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`)
        } else {
          // Handle ISO string format
          expirationDate = new Date(state.exp)
        }
      } else {
        // Handle timestamp format
        expirationDate = new Date(state.exp)
      }

      return new Date() < expirationDate
    },
    isJustVerified: (state) => state.justVerified,
    canAccessDepositSuccess: (state) => state.depositInitiated && state.depositConfirmed,
    canAccessAccountCreated: (state) =>
      state.flowData.canAccessAccountCreated && state.isAuthenticated,
    isInRegistrationFlow: (state) => state.currentFlow === 'registration',
    isInForgotPasswordFlow: (state) => state.currentFlow === 'forgot-password',
  },

  actions: {
    /* -------------------- Initialization -------------------- */
    initialize() {
      // Check if user is authenticated and token is still valid
      if (this.isAuthenticated) {
        this.startExpirationTimer()

        // Fetch fresh balance if authenticated
        this.fetchBalance().catch((err) => {
          console.warn('⚠️ Failed to fetch balance on initialization:', err)
        })
      } else {
        // Clear invalid auth data but don't call full logout to avoid redirect loops
        this.accessToken = ''
        this.exp = null
        this.name = ''
        this.acc_no = ''
        this.bonus = []
        this.balance = 0
        this.clearExpirationTimer()
      }
    },

    /* -------------------- Registration (inline) -------------------- */
    async registerInline(user_id, password, promo_code = '') {
      const ns = useNotificationStore()
      try {
        const { data: response } = await apiClient.post(
          import.meta.env.VITE_API_REGISTER_ENDPOINT,
          {
            user_id,
            password,
            promo_code,
          },
        )

        // Handle the response structure based on your examples
        if (response.code === 'Success' && response.data.code === 200) {
          const acc_no = response.data.data.acc_no

          // Store temporary data for verification
          this.phone = user_id
          this.acc_no = acc_no
          this.tempPassword = password

          ns.showNotification(response.data.message, 'success')
          return { success: true, acc_no }
        } else if (response.code === 'Error') {
          throw new Error(response.data.message || 'Registration failed.')
        } else {
          throw new Error('Registration failed.')
        }
      } catch (err) {
        console.error('[AuthStore] registerInline failed:', err)
        const displayMsg =
          err.response?.data?.data?.message ||
          err.response?.data?.message ||
          err.message ||
          'Registration failed.'
        ns.showNotification(displayMsg, 'error')
        throw err
      }
    },

    /* -------------------- Registration -------------------- */
    async register(user_id, password, promo_code = '', router) {
      const ns = useNotificationStore()
      try {
        const { data: response } = await apiClient.post(
          import.meta.env.VITE_API_REGISTER_ENDPOINT,
          {
            user_id,
            password,
            promo_code,
          },
        )

        // Handle the response structure based on your examples
        if (response.code === 'Success' && response.data.code === 200) {
          const acc_no = response.data.data.acc_no
          this.phone = user_id
          this.acc_no = acc_no
          this.tempPassword = password

          // Set registration flow state
          this.setFlow('registration')

          ns.showNotification(response.data.message, 'success')
        } else if (response.code === 'Error') {
          throw new Error(response.data.message || 'Registration failed.')
        } else {
          throw new Error('Registration failed.')
        }

        await router.push({
          name: 'verify-account',
          params: { acc_no: this.acc_no },
          query: { msisdn: user_id, flow: 'registration' },
        })
      } catch (err) {
        console.error('[AuthStore] register failed:', err)
        const displayMsg =
          err.response?.data?.data?.message ||
          err.response?.data?.message ||
          err.message ||
          'Registration failed.'
        ns.showNotification(displayMsg, 'error')
        throw err
      }
    },

    /* -------------------- Login -------------------- */
    async login(user_id, password) {
      const notificationStore = useNotificationStore()

      try {
        const { data: response } = await apiClient.post(import.meta.env.VITE_API_LOGIN_ENDPOINT, {
          user_id,
          password,
        })

        // Handle the response structure based on your examples
        if (response.code === 'Success' && response.data.code === 200) {
          const { acc_data, bonus, exp } = response.data.data
          this._handleAuthentication(acc_data, bonus, exp, user_id)

          // Show the success message from the API response
          const successMessage =
            response.data.message ||
            `Welcome back, ${acc_data.name || 'User'}! You have been successfully logged in.`
          notificationStore.showNotification(successMessage, 'success')

          await this.fetchBalance()
          return { success: true }
        }

        // Handle verification required (code 425)
        if (response.code === 'Success' && response.data.code === 425) {
          this.phone = user_id
          this.acc_no = response.data.data.acc_no
          notificationStore.showNotification(response.data.message, 'warning')
          return { verify: true, acc_no: response.data.data.acc_no }
        }

        // Handle error responses
        if (response.code === 'Error') {
          throw new Error(response.data.message || 'Login failed.')
        }

        // Handle other cases
        throw new Error('Login failed.')
      } catch (err) {
        console.error('[AuthStore] login failed:', err)
        const displayMsg =
          err.response?.data?.data?.message ||
          err.response?.data?.message ||
          err.message ||
          'Login failed.'
        notificationStore.showNotification(displayMsg, 'error')
        throw err
      }
    },

    /* -------------------- Verification -------------------- */
    async verify(acc_no, verify_code, password = '') {
      const notificationStore = useNotificationStore()
      try {
        const payload = { acc_no, verify_code }
        if (password) payload.password = password

        const { data: response } = await apiClient.post(
          import.meta.env.VITE_API_VERIFY_ENDPOINT,
          payload,
        )

        // Handle the response structure based on your examples
        if (response.code === 'Success' && response.data.code === 200) {
          const { acc_data, bonus, exp } = response.data.data
          this._handleAuthentication(acc_data, bonus, exp, this.phone)

          // Set verification success flags based on current flow
          if (this.currentFlow === 'registration') {
            this.setJustVerified(true)
            this.flowData.canAccessAccountCreated = true
          }

          // Clear the current flow after successful verification
          this.clearFlow()

          notificationStore.showNotification(response.data.message, 'success')
        } else if (response.code === 'Error') {
          throw new Error(response.data.message || 'Verification failed.')
        } else {
          throw new Error('Verification failed.')
        }
      } catch (err) {
        console.error('[AuthStore] verify failed:', err)
        const displayMsg =
          err.response?.data?.data?.message ||
          err.response?.data?.message ||
          err.message ||
          'Verification failed.'
        notificationStore.showNotification(displayMsg, 'error')
        throw err
      }
    },

    /* -------------------- Forgot Password Verification -------------------- */
    async verifyForgotPassword(acc_no, verify_code, new_password) {
      const notificationStore = useNotificationStore()
      try {
        // Use the verify endpoint with the correct payload structure for forgot password
        const { data: response } = await apiClient.post(import.meta.env.VITE_API_VERIFY_ENDPOINT, {
          acc_no,
          verify_code,
          password: new_password,
        })

        // Handle the response structure - should be same as login/verification response
        if (response.code === 'Success' && response.data.code === 200) {
          const { acc_data, bonus, exp } = response.data.data
          this._handleAuthentication(acc_data, bonus, exp, this.phone)

          // Clear the current flow after successful authentication
          this.clearFlow()

          notificationStore.showNotification(response.data.message, 'success')
          return { success: true }
        } else if (response.code === 'Error') {
          throw new Error(response.data.message || 'Password reset failed.')
        } else {
          throw new Error('Password reset failed.')
        }
      } catch (err) {
        console.error('[AuthStore] verifyForgotPassword failed:', err)
        const displayMsg =
          err.response?.data?.data?.message ||
          err.response?.data?.message ||
          err.message ||
          'Password reset failed.'
        notificationStore.showNotification(displayMsg, 'error')
        throw err
      }
    },

    /* -------------------- Forgot Password -------------------- */
    async forgotPassword(user_id, router) {
      const notificationStore = useNotificationStore()
      try {
        const { data: response } = await apiClient.post(
          import.meta.env.VITE_API_RESET_PASSWORD_ENDPOINT,
          { user_id },
        )

        // Handle the response structure based on actual API response
        if (response.code === 'Success' && response.data.code === 200) {
          // Store the user_id and acc_no from the response for the reset process
          this.phone = user_id

          // Extract acc_no from the API response - this is crucial for verification
          const acc_no = response.data.data.acc_no
          this.acc_no = acc_no

          // Set forgot password flow state
          this.setFlow('forgot-password')

          notificationStore.showNotification(response.data.message, 'success')

          // Redirect to verification page with the acc_no from the API response
          await router.push({
            name: 'verify-account',
            params: { acc_no: acc_no },
            query: { msisdn: user_id, flow: 'forgot-password' },
          })

          return { success: true }
        } else if (response.code === 'Error') {
          throw new Error(response.data.message || 'Password reset request failed.')
        } else {
          throw new Error('Password reset request failed.')
        }
      } catch (err) {
        console.error('[AuthStore] forgotPassword failed:', err)
        const displayMsg =
          err.response?.data?.data?.message ||
          err.response?.data?.message ||
          err.message ||
          'Password reset request failed.'
        notificationStore.showNotification(displayMsg, 'error')
        throw err
      }
    },

    /* -------------------- Reset Password -------------------- */
    async resetPassword(user_id, verify_code, new_password) {
      const notificationStore = useNotificationStore()
      try {
        const { data: response } = await apiClient.post(
          import.meta.env.VITE_API_RESET_PASSWORD_ENDPOINT,
          {
            user_id,
            verify_code,
            new_password,
          },
        )

        // Handle the response structure based on your examples
        if (response.code === 'Success' && response.data.code === 200) {
          notificationStore.showNotification(response.data.message, 'success')
          return { success: true }
        } else if (response.code === 'Error') {
          throw new Error(response.data.message || 'Password reset failed.')
        } else {
          throw new Error('Password reset failed.')
        }
      } catch (err) {
        console.error('[AuthStore] resetPassword failed:', err)
        const displayMsg =
          err.response?.data?.data?.message ||
          err.response?.data?.message ||
          err.message ||
          'Password reset failed.'
        notificationStore.showNotification(displayMsg, 'error')
        throw err
      }
    },

    /* -------------------- Internal helper -------------------- */
    async _handleAuthentication(accData, bonusData, expString, user_id) {
      this.exp = dayjs(expString, 'YYYYMMDDHHmmss').toDate()
      this.phone = user_id
      this.name = accData.name || this.name
      this.acc_no = accData.acc_no
      this.accessToken = accData.access_token
      this.bonus = bonusData

      // Set bonuses in the bonus store as well
      const { useBonusStore } = await import('@/stores/useBonusStore')
      const bonusStore = useBonusStore()
      bonusStore.setBonusesFromLogin(bonusData)

      Cookies.set('auth_token', this.accessToken, {
        expires: dayjs(this.exp).diff(dayjs(), 'day'),
        secure: true,
        sameSite: 'Strict',
      })

      this.startExpirationTimer()

      // Auto-fetch user's bet history in the background
      this._autoFetchUserData()
    },

    async _autoFetchUserData() {
      try {
        console.log('🔄 Auto-fetching user data after authentication...')

        // Import bets store dynamically to avoid circular dependency
        const { useBetsStore } = await import('@/stores/bets')
        const betsStore = useBetsStore()

        // Fetch initial bet history in the background
        await betsStore.fetchInitialBets()
        console.log('✅ Auto-fetch completed')
      } catch (error) {
        console.warn('⚠️ Auto-fetch failed (non-critical):', error)
        // Don't throw error as this is a background operation
      }
    },

    /* -------------------- Logout -------------------- */
    logout() {
      // Clear all auth-related state
      this.phone = ''
      this.name = ''
      this.acc_no = ''
      this.accessToken = ''
      this.bonus = []
      this.exp = null
      this.balance = 0
      this.tempPassword = ''
      this.justVerified = false
      this.depositInitiated = false
      this.depositConfirmed = false
      this.lastBetSuccess = false
      this.lastBetStake = 0
      this.lastDepositAmount = null
      this.lastBetOdds = 0
      this.lastBetPayout = 0

      // Clear shared bet state
      this.isSharedBet = false
      this.lastSharedBetCode = ''

      // Clear flow state
      this.clearFlow()

      // Clear cookies
      Cookies.remove('auth_token')

      // Clear expiration timer
      if (this.expirationTimer) {
        clearTimeout(this.expirationTimer)
        this.expirationTimer = null
      }

      // Clear bets store
      this._clearUserData()
    },

    async _clearUserData() {
      try {
        // Import bets store dynamically to avoid circular dependency
        const { useBetsStore } = await import('@/stores/bets')
        const betsStore = useBetsStore()

        // Clear bet history
        betsStore.clearBets()
        console.log('🗑️ Cleared user data stores')
      } catch (error) {
        console.warn('⚠️ Failed to clear user data stores:', error)
      }
    },

    /* -------------------- Balance -------------------- */
    async fetchBalance() {
      try {
        const { data: response } = await apiClient.get(import.meta.env.VITE_API_BALANCE_ENDPOINT)

        // Handle the response structure based on your examples
        if (response.code === 'Success' && response.data.code === 200) {
          this.balance = parseFloat(response.data.data.balance) || 0
          // Also update bonus if available
          if (response.data.data.bonus) {
            this.bonus = response.data.data.bonus
            // Update bonus store as well
            const { useBonusStore } = await import('@/stores/useBonusStore')
            const bonusStore = useBonusStore()
            bonusStore.setBonusesFromLogin(response.data.data.bonus)
          }
        }
      } catch (err) {
        console.error('[AuthStore] fetchBalance failed:', err)
        // Don't show notification for balance fetch failures
      }
    },

    /* -------------------- Timer Management -------------------- */
    startExpirationTimer() {
      if (this.expirationTimer) {
        clearTimeout(this.expirationTimer)
      }

      if (!this.exp) return

      const now = new Date()
      const expirationDate = new Date(this.exp)
      const timeUntilExpiration = expirationDate.getTime() - now.getTime()

      if (timeUntilExpiration > 0) {
        this.expirationTimer = setTimeout(() => {
          const notificationStore = useNotificationStore()
          this.logout()
          notificationStore.showNotification('Session expired. Please log in again.', 'warning')
        }, timeUntilExpiration)
      }
    },

    clearExpirationTimer() {
      if (this.expirationTimer) {
        clearTimeout(this.expirationTimer)
        this.expirationTimer = null
      }
    },

    /* -------------------- State Setters -------------------- */
    setJustVerified(value) {
      this.justVerified = value
    },

    setDepositInitiated(value) {
      this.depositInitiated = value
    },

    setDepositConfirmed(value) {
      this.depositConfirmed = value
    },

    setLastBetSuccess(success) {
      this.lastBetSuccess = success
    },

    setLastBetStake(stake) {
      this.lastBetStake = stake
    },

    setLastBetOdds(odds) {
      this.lastBetOdds = odds
    },

    setLastBetPayout(payout) {
      this.lastBetPayout = payout
    },

    setLastDepositAmount(amount) {
      this.lastDepositAmount = amount
    },

    /* -------------------- Shared Bet Management -------------------- */
    setIsSharedBet(value) {
      this.isSharedBet = value
    },

    setLastSharedBetCode(code) {
      this.lastSharedBetCode = code
    },

    resetSharedBetState() {
      this.isSharedBet = false
      this.lastSharedBetCode = ''
    },

    /* -------------------- Flow Management -------------------- */
    setFlow(flowType) {
      this.currentFlow = flowType
      this.flowData.fromRegistration = flowType === 'registration'
      this.flowData.fromForgotPassword = flowType === 'forgot-password'
      this.flowData.canAccessAccountCreated = false
    },

    clearFlow() {
      this.currentFlow = null
      this.flowData.fromRegistration = false
      this.flowData.fromForgotPassword = false
      // Don't clear canAccessAccountCreated here - it should persist until used
    },

    clearAccountCreatedAccess() {
      this.flowData.canAccessAccountCreated = false
    },
  },

  persist: {
    key: 'auth',
    storage: localStorage,
    paths: [
      'phone',
      'name',
      'acc_no',
      'accessToken',
      'bonus',
      'exp',
      'balance',
      'tempPassword',
      'justVerified',
      'depositInitiated',
      'depositConfirmed',
      'lastBetSuccess',
      'lastBetStake',
      'lastDepositAmount',
      'lastBetOdds',
      'lastBetPayout',
      'isSharedBet',
      'lastSharedBetCode',
      'currentFlow',
      'flowData',
    ],
  },
})
